// Binders and sealed product: the reads (TanStack queries) and the writes
// (through useCopiesWriteRunner, so every one gets the per-copy rules: the
// capability gate, the write fence, ONE op_key per action re-sent only by
// that action's retries, the ambiguous-write reconcile and the kept Retry
// intents).
//
//  - Every query is enabled only while the capability is per_copy. A binders
//    read that fails (503 binders_not_ready, a dark 404, anything) hides the
//    binders UI; it never moves the capability (collectionBinders.ts).
//  - The rule preview is cached by its canonical rules and only asked for
//    after the caller's debounce, which keeps it inside the server's 10/min.
//    A preview whose rules name the spare rule also keys on the deck
//    generation (lib/deckDependentCollection): after a My Decks change it is
//    asked again, and an older in-flight answer lands under a key nobody reads.
import { useMemo, useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  addSealed, createBinder, deleteBinder, deleteSealed, fetchSealedCatalog, listBinders, listSealed, openSealed,
  orderBinders, patchBinder, patchSealed, previewBinder,
  type BinderCreateBody, type BinderPatchBody, type BinderPreview, type BinderRules, type BinderWriteResponse,
  type BindersList, type SealedAddBody, type SealedList, type SealedOpenResponse, type SealedPatchBody,
  type SealedProduct, type SealedWriteResponse,
} from '../api/collectionBinders'
import { isCopiesError, type SetFacet } from '../api/collectionCopies'
import { useCopiesFacets } from './useCopies'
import { CAPABILITY, ERR, OP_KIND, QK, type Capability } from '../api/collectionCopies.codes'
import { useCopiesWriteRunner, type WriteResult } from './useCopyMutations'
import { PREVIEW_DEFAULT_BLOCK_MS, previewLimiter, retryAfterUncapped } from '../lib/previewLimiter'
import { deckGeneration, previewDeckSegment, subscribeDeckGeneration } from '../lib/deckDependentCollection'

export function bindersQueryKey(collectionId: string | null, userId: string | null) {
  return [QK.binders, collectionId, userId] as const
}

export function sealedQueryKey(collectionId: string | null, userId: string | null) {
  return [QK.sealed, collectionId, userId] as const
}

export function useBinders(collectionId: string | null, userId: string | null, mode: Capability) {
  const q = useQuery<BindersList>({
    queryKey: bindersQueryKey(collectionId, userId),
    enabled: mode === CAPABILITY.perCopy && !!collectionId && !!userId,
    retry: false,
    staleTime: 30_000,
    queryFn: ({ signal }) => listBinders({ userId: userId!, collectionId: collectionId!, signal }),
  })
  const notReady = isCopiesError(q.error) && q.error.kind === ERR.bindersNotReady
  // A background re-read failed while a list is held (say the re-read after
  // a My Decks change): the held list stays on screen with a Retry. Not for
  // binders_not_ready, which hides the binders UI as before, and not for
  // ANY HTTP 404 (the binders gate turned dark: a generic 404 classifies as
  // a server error, so the status is read): that falls back to Cards.
  const dark = isCopiesError(q.error) && q.error.status === 404
  const refreshFailed = q.isError && q.data !== undefined && !notReady && !dark
  return { ...q, notReady, refreshFailed }
}

export function useSealed(collectionId: string | null, userId: string | null, mode: Capability) {
  return useQuery<SealedList>({
    queryKey: sealedQueryKey(collectionId, userId),
    enabled: mode === CAPABILITY.perCopy && !!collectionId && !!userId,
    retry: false,
    staleTime: 30_000,
    queryFn: ({ signal }) => listSealed({ userId: userId!, collectionId: collectionId!, signal }),
  })
}

/** Canonical JSON of a rule set: the preview's cache key. */
export function rulesCacheKey(rules: BinderRules): string {
  return JSON.stringify({ match: rules.match, conditions: rules.conditions })
}

/**
 * The preview's query key: the rules LAST (lib/deckDependentCollection reads
 * them there), after the deck generation segment (null unless the rules name
 * the spare rule).
 */
export function binderPreviewQueryKey(collectionId: string | null, userId: string | null, rulesKey: string | null) {
  return [QK.binderPreview, collectionId, userId, previewDeckSegment(rulesKey), rulesKey] as const
}

/** The live "N of your cards match" count. Pass rules only after debouncing. */
export function useBinderPreview(collectionId: string | null, userId: string | null, mode: Capability, rules: BinderRules | null) {
  // Re-render on a deck change: a spare preview's key moves with it.
  useSyncExternalStore(subscribeDeckGeneration, deckGeneration, deckGeneration)
  const rulesKey = rules ? rulesCacheKey(rules) : null
  return useQuery<BinderPreview>({
    queryKey: binderPreviewQueryKey(collectionId, userId, rulesKey),
    enabled: mode === CAPABILITY.perCopy && !!collectionId && !!userId && !!rules,
    // Never re-asked on its own: no retry, no refetch on focus, mount or
    // reconnect, and the same rules are answered from the cache for the
    // sitting (the server allows 10 a minute).
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async ({ signal }) => {
      // A rolling window (<= 9 per 60 s). A query whose rules were replaced
      // is cancelled while it waits, so only the newest rules are ever sent.
      await previewLimiter.acquire(signal)
      try {
        return await previewBinder({ userId: userId!, collectionId: collectionId!, signal }, rules!)
      } catch (e) {
        if (isCopiesError(e) && e.kind === ERR.rateLimited) {
          // Honor the server's wait in full before the next preview.
          previewLimiter.blockFor(retryAfterUncapped(e.retryAfter) ?? PREVIEW_DEFAULT_BLOCK_MS)
        }
        throw e
      }
    },
  })
}

/** No status filter: the set picker lists every set the collection holds. */
const ALL_STATUSES: readonly string[] = []

/**
 * The sets this collection owns, with names (the smart rule's set picker and
 * the rule summary's set names): the copies facets query (useCopiesFacets)
 * over every status. `enabled` lets a caller ask only when a set rule is on
 * screen. `failed` = the read failed or the server sent no facets: the
 * callers fall back to plain codes.
 */
export function useOwnedSets(collectionId: string | null, userId: string | null, mode: Capability, enabled = true) {
  const q = useCopiesFacets(collectionId, userId, mode, ALL_STATUSES, enabled)
  const sets = useMemo<SetFacet[] | undefined>(
    () => (q.data ? q.data.sets.map(s => ({ ...s, code: s.code.toLowerCase() })) : undefined),
    [q.data],
  )
  return { sets, failed: q.isError || q.data === null }
}

/** code -> name of the owned sets whose name is known. */
export function useOwnedSetNames(collectionId: string | null, userId: string | null, mode: Capability, enabled = true): ReadonlyMap<string, string> | undefined {
  const { sets } = useOwnedSets(collectionId, userId, mode, enabled)
  return useMemo(() => (sets ? new Map(sets.filter(s => s.name).map(s => [s.code, s.name as string])) : undefined), [sets])
}

/** The public sealed catalog search (q >= 2 characters; debounce in the caller). */
export function useSealedCatalog(q: string, userId: string | null) {
  const query = q.trim()
  return useQuery<SealedProduct[]>({
    queryKey: [QK.sealedCatalog, query],
    enabled: query.length >= 2,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) => fetchSealedCatalog(query, { userId, signal }),
  })
}

export interface BinderMutations {
  create: (body: BinderCreateBody) => Promise<WriteResult<BinderWriteResponse>>
  patch: (binderId: number, body: BinderPatchBody) => Promise<WriteResult<BinderWriteResponse>>
  remove: (binderId: number) => Promise<WriteResult<void>>
  order: (binderIds: number[], expectedRev: number) => Promise<WriteResult<{ binders_rev: number }>>
  addSealed: (body: SealedAddBody) => Promise<WriteResult<SealedWriteResponse>>
  patchSealed: (sealedId: number, body: SealedPatchBody) => Promise<WriteResult<SealedWriteResponse>>
  removeSealed: (sealedId: number) => Promise<WriteResult<void>>
  openSealed: (sealedId: number, qty: number) => Promise<WriteResult<SealedOpenResponse>>
}

const NAMES_UNCHANGED = () => false

export function useBinderMutations(collectionId: string | null, userId: string | null): BinderMutations {
  const { run } = useCopiesWriteRunner(collectionId, userId)
  return useMemo<BinderMutations>(() => ({
    create: body => run({
      withOpKey: true, kind: OP_KIND.binderCreate, namesChanged: NAMES_UNCHANGED,
      send: (ctx, opKey) => createBinder(ctx, body, opKey),
    }),
    patch: (binderId, body) => run({
      withOpKey: true, kind: OP_KIND.binderPatch, namesChanged: NAMES_UNCHANGED,
      send: (ctx, opKey) => patchBinder(ctx, binderId, body, opKey),
    }),
    // DELETE is keyless and idempotent, like DELETE /copies/{id}. Its lines go
    // Unsorted (a membership change: the owned names never move).
    remove: binderId => run<void>({
      withOpKey: false, kind: OP_KIND.binderDelete, namesChanged: NAMES_UNCHANGED,
      send: ctx => deleteBinder(ctx, binderId),
    }),
    order: (binderIds, expectedRev) => run({
      withOpKey: true, kind: OP_KIND.binderOrder, namesChanged: NAMES_UNCHANGED,
      send: (ctx, opKey) => orderBinders(ctx, binderIds, expectedRev, opKey),
    }),
    // Sealed rows are never in HAVE: no owned-names revalidation, ever.
    addSealed: body => run({
      withOpKey: true, kind: OP_KIND.sealedAdd, namesChanged: NAMES_UNCHANGED,
      send: (ctx, opKey) => addSealed(ctx, body, opKey),
    }),
    patchSealed: (sealedId, body) => run({
      withOpKey: true, kind: OP_KIND.sealedPatch, namesChanged: NAMES_UNCHANGED,
      send: (ctx, opKey) => patchSealed(ctx, sealedId, body, opKey),
    }),
    removeSealed: sealedId => run<void>({
      withOpKey: false, kind: OP_KIND.sealedDelete, namesChanged: NAMES_UNCHANGED,
      send: ctx => deleteSealed(ctx, sealedId),
    }),
    openSealed: (sealedId, qty) => run({
      withOpKey: true, kind: OP_KIND.sealedOpen, namesChanged: NAMES_UNCHANGED,
      send: (ctx, opKey) => openSealed(ctx, sealedId, qty, opKey),
    }),
  }), [run])
}
